/**
 * FairwayBid – Angebot als Dokument
 *
 * Ein Angebot lebt bisher nur als Zeile im Posteingang. Eine Golfanlage
 * braucht es aber auf Papier oder als PDF: für den Vorstand, für die
 * Ablage, für den Vergleich mit einem Angebot, das per Post kam.
 *
 * Dieses Modul baut aus einer Transaktion ein A4-Dokument mit allen
 * Angaben, die der Anbieter beim Absenden gemacht hat. Bewusst ohne
 * PDF-Bibliothek: Der Browser druckt das HTML nach PDF, und das
 * Dokument bleibt lesbar, auch wenn niemand mehr ein Paket pflegt.
 *
 * Reine Zeichenkettenarbeit, kein React – damit prüfbar.
 */

import { escapeHtml, formatDate, providerName, buildDocument } from './html';
import { OFFER_FIELDS, getOfferFieldValue, getOfferFieldRaw } from './offerFields';
import { getOfferAmount, formatAmount, getOfferStatus, STATUS_LABELS } from './offers';
import { getDeadlineStatus } from './deadlines';
import { getCategoryLabel } from './matching';
import { describeCoverage } from './coverage';

/** Wann ist das Angebot eingegangen? */
export const getOfferDate = tx =>
  tx?.attributes?.lastTransitionedAt || tx?.attributes?.createdAt || null;

/**
 * Die Angaben des Anbieters als Zeilenliste – dieselbe Reihenfolge wie in
 * der Vergleichstabelle und im Vergabevermerk, damit man nicht umdenken muss.
 */
export const buildOfferRows = (offer, listing = null) => {
  const country = listing?.attributes?.publicData?.delivery_country || null;
  return [
    { label: 'Gesamtpreis netto', value: formatAmount(getOfferAmount(offer)) || 'auf Anfrage' },
    ...OFFER_FIELDS.map(field => ({
      label: field.label,
      value: getOfferFieldValue(field.key, getOfferFieldRaw(offer, field.key)) || '–',
    })),
    { label: 'Liefergebiet und Service', value: describeCoverage(offer?.provider, country) },
    { label: 'Status', value: STATUS_LABELS[getOfferStatus(offer)] || getOfferStatus(offer) },
  ];
};

/**
 * Druckfertiges HTML eines einzelnen Angebots.
 *
 * @param {Object} params
 * @param {Object} params.offer Die Transaktion mit den Angebotsangaben
 * @param {Object} params.listing Die Anfrage, auf die geboten wurde
 * @param {string} params.clubName Name der ausschreibenden Anlage
 * @param {number} params.total Gesamtzahl der Angebote zur Anfrage, optional
 * @param {Date} params.now Erstellungszeitpunkt
 * @returns {string} vollständiges HTML-Dokument
 */
export const buildOfferHtml = ({
  offer,
  listing,
  clubName = '',
  total = null,
  now = new Date(),
} = {}) => {
  if (!offer) {
    return buildDocument({
      title: 'Angebot',
      body: '<h1>Angebot</h1><p>Dieses Angebot ist nicht mehr verfügbar.</p>',
    });
  }

  const anbieter = providerName(offer);
  const titel = listing?.attributes?.title || 'Bedarfsanfrage';
  const frist = getDeadlineStatus(listing, now);
  const rows = buildOfferRows(offer, listing);
  const preis = formatAmount(getOfferAmount(offer));

  // Bewusst ohne laufende Nummer: Im Posteingang stehen die Angebote nach
  // Eingang, in Vergleich und Anzeigenliste nach Preis. Eine Nummer waere
  // dann je nach Herkunft eine andere - der Anbietername ist eindeutig.
  const zaehler =
    total && total > 1 ? `Dieses Angebot – eines von ${total} zur Anfrage` : 'Angebot';

  const tabelle = rows
    .map(
      row =>
        `<tr><th class="crit">${escapeHtml(row.label)}</th><td>${escapeHtml(row.value)}</td></tr>`
    )
    .join('');

  const body = `
  <h1>Angebot von ${escapeHtml(anbieter)}</h1>
  <p class="sub">${escapeHtml(titel)}</p>

  ${
    preis
      ? `<p class="decision"><strong>${escapeHtml(
          preis
        )} netto</strong> – Gesamtpreis laut Angebot.</p>`
      : ''
  }

  <h2>Anfrage</h2>
  <dl>
    <dt>Ausschreibende Stelle</dt><dd>${escapeHtml(clubName || '–')}</dd>
    <dt>Kategorie</dt><dd>${escapeHtml(getCategoryLabel(listing))}</dd>
    <dt>Veröffentlicht am</dt><dd>${escapeHtml(
      formatDate(listing?.attributes?.publishedAt || listing?.attributes?.createdAt)
    )}</dd>
    <dt>Angebotsfrist</dt><dd>${escapeHtml(frist ? frist.dateLabel : '–')}</dd>
  </dl>

  <h2>${escapeHtml(zaehler)}</h2>
  <dl>
    <dt>Anbieter</dt><dd>${escapeHtml(anbieter)}</dd>
    <dt>Eingegangen am</dt><dd>${escapeHtml(formatDate(getOfferDate(offer)))}</dd>
  </dl>

  <table><tbody>${tabelle}</tbody></table>

  <footer>
    Erstellt am ${escapeHtml(formatDate(now))} über FairwayBid. Die Angaben stammen aus dem
    Angebot des Anbieters; für ihre Richtigkeit ist der Anbieter verantwortlich. Die
    Bindungsfrist richtet sich nach der Angabe unter „Angebot gültig“. Lieferung, Rechnung
    und Zahlung laufen direkt zwischen dem Anbieter und der Golfanlage; FairwayBid wickelt
    keine Zahlungen ab.
  </footer>`;

  return buildDocument({ title: `Angebot ${anbieter} – ${titel}`, body });
};
